/**
 * Pure portfolio merge helpers (no HTTP client) — testable without Vite env.
 */

import type { ParsedPortfolio } from '../../miniapp/api/hyperliquidDirect';
import { oroPositionCoinKey } from './portfolioPairs';

export type OroVenueKey = 'hyperliquid' | 'propr' | string;

export type OroExchangeBreakdown = {
  exchange: OroVenueKey;
  positions: number;
  unrealizedPnL: number;
  marginUsed: number;
  accountValue: number;
  totalEquity?: number;
  availableEquity?: number;
  status: 'connected' | 'error' | 'no_api' | string;
};

export type OroPosition = {
  id: string;
  pair: string;
  side: 'LONG' | 'SHORT';
  size: number;
  entryPrice: number;
  markPrice: number;
  unrealizedPnL: number;
  unrealizedPnLPercent: number;
  exchange: OroVenueKey;
  leverage?: number;
  marginUsed?: number;
  stopLossPrice?: number;
  takeProfitPrice?: number;
  liquidationPrice?: number;
  sourceStrategyId?: string;
  sourceStrategyName?: string;
};

export type OroPortfolioDashboard = {
  totalPortfolioValue: number;
  totalUnrealizedPnL: number;
  totalRealizedPnL: number;
  totalPositions: number;
  loadState?: 'ok' | 'stale' | 'partial' | 'unavailable';
  exchangeBreakdown: OroExchangeBreakdown[];
  summary: {
    totalPositions: number;
    totalUnrealizedPnL: number;
    totalMarginUsed: number;
    positions: OroPosition[];
  };
};

export type OroNonHlSlice = {
  success?: boolean;
  partial?: boolean;
  positions?: any[];
  totalPositions?: number;
  totalUnrealizedPnL?: number;
  totalMarginUsed?: number;
  totalAccountValue?: number;
  exchangeBreakdown?: Array<{
    exchange: string;
    positions?: number;
    unrealizedPnL?: number;
    marginUsed?: number;
    accountValue?: number;
    availableEquity?: number;
    status?: string;
  }>;
  venueErrors?: Array<{ venue?: string; exchange?: string; message?: string }>;
};

function normVenue(raw: unknown): OroVenueKey {
  const s = String(raw || '')
    .trim()
    .toLowerCase();
  if (s.includes('propr')) return 'propr';
  if (s.includes('hyper') || s === 'hl') return 'hyperliquid';
  return s || 'unknown';
}

export function mapOroPosition(p: any): OroPosition {
  const sideRaw = String(p?.side || p?.positionSide || 'long').toLowerCase();
  const entryPrice = Number(p?.entryPrice || p?.entry || p?.averageEntry || 0) || 0;
  const markPrice = Number(p?.markPrice || p?.mark || p?.oraclePrice || 0) || 0;
  const sizeCoins = Number(p?.size ?? p?.baseSize ?? p?.qty ?? p?.quantity ?? NaN);
  const sizeUsd = Number(p?.sizeUsd || p?.notional || p?.notionalValue || 0) || 0;
  let size = Number.isFinite(sizeCoins) && Math.abs(sizeCoins) > 0 ? Math.abs(sizeCoins) : 0;
  if (!(size > 0) && sizeUsd > 0 && (markPrice > 0 || entryPrice > 0)) {
    size = sizeUsd / (markPrice || entryPrice);
  }
  return {
    id: String(p?.id || `${p?.pair || p?.asset || 'x'}-${sideRaw}`),
    pair: String(p?.pair || (p?.asset ? `${p.asset}-USD` : 'BTC-USD')),
    side: sideRaw.startsWith('s') ? 'SHORT' : 'LONG',
    size,
    entryPrice,
    markPrice,
    unrealizedPnL: Number(p?.unrealizedPnL || p?.uPnl || 0) || 0,
    unrealizedPnLPercent: Number(p?.unrealizedPnLPercent || p?.pnlPercent || 0) || 0,
    exchange: normVenue(p?.exchange),
    leverage: Number(p?.leverage || 0) || undefined,
    marginUsed: Number(p?.marginUsed || 0) || undefined,
    stopLossPrice: Number(p?.stopLossPrice || p?.sl || 0) || undefined,
    takeProfitPrice: Number(p?.takeProfitPrice || p?.tp || 0) || undefined,
    liquidationPrice: Number(p?.liquidationPrice || 0) || undefined,
    sourceStrategyId: p?.sourceStrategyId ? String(p.sourceStrategyId) : undefined,
    sourceStrategyName: p?.sourceStrategyName ? String(p.sourceStrategyName) : undefined,
  };
}

function mapHlToOroPositions(hl: ParsedPortfolio): OroPosition[] {
  return (hl.positions || []).map((p) => {
    const side = p.side === 'short' ? ('SHORT' as const) : ('LONG' as const);
    return {
      id: `hl:${String(p.pair || '').toUpperCase()}`,
      pair: p.pair,
      side,
      size: p.size,
      entryPrice: p.entryPrice,
      markPrice: p.currentPrice,
      unrealizedPnL: p.pnl,
      unrealizedPnLPercent: p.pnlPercent,
      exchange: 'hyperliquid' as const,
      leverage: p.leverage,
      marginUsed: p.marginUsed,
      liquidationPrice: p.liquidationPrice ?? undefined,
    };
  });
}

/** Preserve server-stamped SL/TP when live HL overlays refresh mark/size only. */
export function carryPositionProtection(
  next: OroPosition[],
  prev: OroPosition[] | undefined,
): OroPosition[] {
  if (!prev?.length) return next;
  const byId = new Map<string, OroPosition>();
  const byCoinSide = new Map<string, OroPosition>();
  for (const p of prev) {
    if (p.exchange !== 'hyperliquid') continue;
    if (!p.stopLossPrice && !p.takeProfitPrice) continue;
    byId.set(p.id, p);
    const coin = oroPositionCoinKey(p.pair);
    if (coin) byCoinSide.set(`${coin}:${p.side}`, p);
  }
  if (byId.size === 0 && byCoinSide.size === 0) return next;
  return next.map((p) => {
    if (p.exchange !== 'hyperliquid') return p;
    if (p.stopLossPrice || p.takeProfitPrice) return p;
    const coin = oroPositionCoinKey(p.pair);
    const old = byId.get(p.id) || (coin ? byCoinSide.get(`${coin}:${p.side}`) : undefined);
    if (!old) return p;
    return {
      ...p,
      stopLossPrice: old.stopLossPrice,
      takeProfitPrice: old.takeProfitPrice,
    };
  });
}

/** Keep strategy hints across live HL overlays — those payloads have no attribution. */
export function carryPositionStrategyAttribution(
  next: OroPosition[],
  prev: OroPosition[] | undefined,
): OroPosition[] {
  if (!prev?.length) return next;
  const byId = new Map<string, OroPosition>();
  const byCoinSide = new Map<string, OroPosition>();
  for (const p of prev) {
    if (!p.sourceStrategyId && !p.sourceStrategyName) continue;
    byId.set(p.id, p);
    const coin = oroPositionCoinKey(p.pair);
    if (coin) byCoinSide.set(`${coin}:${p.side}`, p);
  }
  if (byId.size === 0 && byCoinSide.size === 0) return next;
  return next.map((p) => {
    if (p.sourceStrategyId || p.sourceStrategyName) return p;
    const coin = oroPositionCoinKey(p.pair);
    const old = byId.get(p.id) || (coin ? byCoinSide.get(`${coin}:${p.side}`) : undefined);
    if (!old) return p;
    return {
      ...p,
      sourceStrategyId: old.sourceStrategyId,
      sourceStrategyName: old.sourceStrategyName,
    };
  });
}

/** Replace HL rows in a server dashboard with browser-direct HL portfolio data. */
export function mergeHlDirectIntoDashboard(
  dash: OroPortfolioDashboard,
  hl: ParsedPortfolio,
  orders: any[],
  hlExecutionReady = true,
): OroPortfolioDashboard {
  const hlPositions = carryPositionProtection(
    carryPositionStrategyAttribution(mapHlToOroPositions(hl), dash.summary.positions),
    dash.summary.positions,
  );
  const nonHl = (dash.summary.positions || []).filter((p) => p.exchange !== 'hyperliquid');
  const positions = [...nonHl, ...hlPositions];
  const hlUnrealized = hlPositions.reduce((s, p) => s + (p.unrealizedPnL || 0), 0);
  const nonHlUnrealized = nonHl.reduce((s, p) => s + (p.unrealizedPnL || 0), 0);
  const totalUnrealizedPnL = hlUnrealized + nonHlUnrealized;

  const breakdown = (dash.exchangeBreakdown || []).map((row) => {
    if (row.exchange !== 'hyperliquid') return row;
    return {
      ...row,
      positions: hlPositions.length,
      unrealizedPnL: hlUnrealized,
      marginUsed: hl.marginUsed || row.marginUsed,
      accountValue: hl.equity > 0 ? hl.equity : row.accountValue,
      totalEquity: hl.equity > 0 ? hl.equity : row.totalEquity,
      availableEquity: hl.withdrawable > 0 ? hl.withdrawable : row.availableEquity,
      status: hlExecutionReady ? ('connected' as const) : ('no_api' as const),
    };
  });

  const hasHlRow = breakdown.some((r) => r.exchange === 'hyperliquid');
  const nextBreakdown = hasHlRow
    ? breakdown
    : [
        ...breakdown,
        {
          exchange: 'hyperliquid' as OroVenueKey,
          positions: hlPositions.length,
          unrealizedPnL: hlUnrealized,
          marginUsed: hl.marginUsed || 0,
          accountValue: hl.equity || 0,
          totalEquity: hl.equity || 0,
          availableEquity: hl.withdrawable || 0,
          status: hlExecutionReady ? ('connected' as const) : ('no_api' as const),
        },
      ];

  const totalPortfolioValue = nextBreakdown.reduce(
    (s, r) => s + (Number(r.accountValue) || 0),
    0,
  );
  const totalMarginUsed = nextBreakdown.reduce((s, r) => s + (Number(r.marginUsed) || 0), 0);

  return {
    ...dash,
    totalPortfolioValue,
    totalUnrealizedPnL,
    totalPositions: positions.length,
    exchangeBreakdown: nextBreakdown,
    summary: {
      ...dash.summary,
      totalPositions: positions.length,
      totalUnrealizedPnL,
      totalMarginUsed,
      positions,
    },
  };
}

/** Overlay Propr/non-HL slice onto a dashboard without double-counting venues already present. */
export function overlayNonHlOntoDashboard(
  dash: OroPortfolioDashboard,
  slice: OroNonHlSlice | null | undefined,
): OroPortfolioDashboard {
  if (!slice?.success) return dash;
  const extraBreakdown = Array.isArray(slice.exchangeBreakdown) ? slice.exchangeBreakdown : [];
  const hasVenueErrors = Array.isArray(slice.venueErrors) && slice.venueErrors.length > 0;
  if (
    !extraBreakdown.length &&
    !(Array.isArray(slice.positions) && slice.positions.length) &&
    !hasVenueErrors
  ) {
    return dash;
  }

  const byEx = new Map<string, OroExchangeBreakdown>();
  for (const row of dash.exchangeBreakdown || []) {
    byEx.set(String(row.exchange), row);
  }
  for (const raw of extraBreakdown) {
    const exchange = normVenue(raw.exchange);
    const prev = byEx.get(exchange);
    const accountValue = Number(raw.accountValue || 0) || Number(prev?.accountValue || 0) || 0;
    const availableRaw = Number(raw.availableEquity);
    const availableEquity =
      Number.isFinite(availableRaw) && availableRaw > 0
        ? availableRaw
        : prev?.availableEquity;
    const positions = Math.max(Number(raw.positions || 0) || 0, Number(prev?.positions || 0) || 0);
    const useRawPositions = Number(raw.positions || 0) >= Number(prev?.positions || 0);
    byEx.set(exchange, {
      exchange,
      positions,
      unrealizedPnL: useRawPositions
        ? Number(raw.unrealizedPnL || 0) || 0
        : Number(prev?.unrealizedPnL || 0) || 0,
      marginUsed: useRawPositions
        ? Number(raw.marginUsed || 0) || 0
        : Number(prev?.marginUsed || 0) || 0,
      accountValue,
      totalEquity: Number(raw.totalEquity || raw.accountValue || 0) || prev?.totalEquity || accountValue,
      availableEquity,
      status:
        String(raw.status || '') === 'connected' || prev?.status === 'connected'
          ? 'connected'
          : String(raw.status || prev?.status || 'error'),
    });
  }

  if (Array.isArray(slice.venueErrors)) {
    for (const ve of slice.venueErrors) {
      const exchange = normVenue(ve?.venue || ve?.exchange);
      if (!exchange || exchange === 'unknown') continue;
      const prev = byEx.get(exchange);
      byEx.set(exchange, {
        exchange,
        positions: Number(prev?.positions || 0) || 0,
        unrealizedPnL: Number(prev?.unrealizedPnL || 0) || 0,
        marginUsed: Number(prev?.marginUsed || 0) || 0,
        accountValue: Number(prev?.accountValue || 0) || 0,
        totalEquity: prev?.totalEquity,
        availableEquity: prev?.availableEquity,
        status: 'error',
      });
    }
  }

  const nonHlExchanges = new Set(extraBreakdown.map((r) => normVenue(r.exchange)));
  const kept = (dash.summary.positions || []).filter((p) => !nonHlExchanges.has(p.exchange));
  const extraPos = (Array.isArray(slice.positions) ? slice.positions : []).map(mapOroPosition);
  const positions = [...kept, ...extraPos];
  const breakdown = Array.from(byEx.values());
  const totalPortfolioValue = breakdown.reduce((s, r) => s + (Number(r.accountValue) || 0), 0);
  const totalUnrealizedPnL = positions.reduce((s, p) => s + (p.unrealizedPnL || 0), 0);
  const totalMarginUsed = breakdown.reduce((s, r) => s + (Number(r.marginUsed) || 0), 0);
  const loadState =
    slice.partial || dash.loadState === 'partial'
      ? 'partial'
      : dash.loadState === 'unavailable'
        ? 'unavailable'
        : dash.loadState;

  return {
    ...dash,
    totalPortfolioValue,
    totalUnrealizedPnL,
    totalPositions: positions.length,
    loadState,
    exchangeBreakdown: breakdown,
    summary: {
      totalPositions: positions.length,
      totalUnrealizedPnL,
      totalMarginUsed,
      positions,
    },
  };
}
